import { useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AnimalReportFields,
  type ChipStatus,
} from './AnimalReportFields'
import { AnimalTypePicker } from './AnimalTypePicker'
import { PhotoPicker } from './PhotoPicker'
import { SightingDateField, SightingTimeField } from './SightingDateTimePickers'
import {
  TEMPERAMENTS,
  fixedToSpayed,
  type AnimalType,
  type ColorKey,
  type FixedOption,
  type Gender,
  type Temperament,
} from '../lib/constants'
import {
  createSighting,
  uploadSightingImage,
  type MapSighting,
} from '../lib/sightingsApi'
import { prepareImagesForUpload } from '../lib/prepareImage'
import { resolvePostCreateNotifications } from '../lib/pushNotifications'
import { NotificationOptInPrompt } from './NotificationOptInPrompt'

export interface SightingLocation {
  lat: number
  lng: number
}

interface Props {
  open: boolean
  onClose: () => void
  pickedMapLocation: SightingLocation | null
  onRequestMapPick: () => void
  onSubmitted: (sighting: MapSighting) => void
}

function localDateValue(date = new Date()) {
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

function localTimeValue(date = new Date()) {
  return date.toTimeString().slice(0, 5)
}

function formatCoordinates(location: SightingLocation) {
  return `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`
}

function toSightedAt(date: string, time: string) {
  return new Date(`${date}T${time}:00`).toISOString()
}

export function AddSightingModal({
  open,
  onClose,
  pickedMapLocation,
  onRequestMapPick,
  onSubmitted,
}: Props) {
  const { t, i18n } = useTranslation()
  const [type, setType] = useState<AnimalType | ''>('')
  const [color1, setColor1] = useState('')
  const [color2, setColor2] = useState('')
  const [color3, setColor3] = useState('')
  const [chipStatus, setChipStatus] = useState<ChipStatus>('unknown')
  const [chip, setChip] = useState('')
  const [gender, setGender] = useState<Gender>('unknown')
  const [fixed, setFixed] = useState<FixedOption>('unknown')
  const [notes, setNotes] = useState('')
  const [temperament, setTemperament] = useState<Temperament>('not_sure')
  const [images, setImages] = useState<File[]>([])
  const [showPhotoError, setShowPhotoError] = useState(false)
  const [showTypeError, setShowTypeError] = useState(false)
  const [dateMode, setDateMode] = useState<'today' | 'custom'>('today')
  const [selectedDate, setSelectedDate] = useState(localDateValue)
  const [timeMode, setTimeMode] = useState<'now' | 'custom'>('now')
  const [selectedTime, setSelectedTime] = useState(localTimeValue)
  const [locationMode, setLocationMode] = useState<'current' | 'map' | null>(null)
  const [location, setLocation] = useState<SightingLocation | null>(null)
  const [locationPending, setLocationPending] = useState(false)
  const [locationError, setLocationError] = useState(false)
  const [willingToFoster, setWillingToFoster] = useState(false)
  const [willingToAdopt, setWillingToAdopt] = useState(false)
  const [showCareInformation, setShowCareInformation] = useState(false)
  const [notifyPrompt, setNotifyPrompt] = useState<{
    id: number
    type: AnimalType
  } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const today = localDateValue()
  const isDifferentDate = selectedDate !== today

  useEffect(() => {
    if (!pickedMapLocation) return
    setLocation(pickedMapLocation)
    setLocationMode('map')
    setLocationError(false)
  }, [pickedMapLocation])

  useEffect(() => {
    if (!open) return
    if (dateMode === 'today') setSelectedDate(localDateValue())
    if (timeMode === 'now') setSelectedTime(localTimeValue())
  }, [open, dateMode, timeMode])

  useEffect(() => {
    if (images.length > 0) setShowPhotoError(false)
  }, [images])

  function resetForm() {
    setType('')
    setColor1('')
    setColor2('')
    setColor3('')
    setChipStatus('unknown')
    setChip('')
    setGender('unknown')
    setFixed('unknown')
    setNotes('')
    setTemperament('not_sure')
    setImages([])
    setShowPhotoError(false)
    setShowTypeError(false)
    setDateMode('today')
    setSelectedDate(localDateValue())
    setTimeMode('now')
    setSelectedTime(localTimeValue())
    setLocationMode(null)
    setLocation(null)
    setLocationPending(false)
    setLocationError(false)
    setWillingToFoster(false)
    setWillingToAdopt(false)
    setShowCareInformation(false)
    setNotifyPrompt(null)
    setSubmitting(false)
    setSubmitError(null)
  }

  function finishAfterCare() {
    setShowCareInformation(false)
    resetForm()
    onClose()
  }

  function finishAfterNotify() {
    setNotifyPrompt(null)
    if (willingToFoster || willingToAdopt) {
      setShowCareInformation(true)
      setSubmitting(false)
    } else {
      resetForm()
      onClose()
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (submitting) return

    const missingPhotos = images.length === 0
    const missingType = !type
    setShowPhotoError(missingPhotos)
    setShowTypeError(missingType)
    if (missingPhotos || missingType || !location || !color1) {
      if (!location) setLocationError(true)
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    try {
      const prepared = await prepareImagesForUpload(images)
      const created = await createSighting({
        type,
        color1: color1 as ColorKey,
        color2: (color2 || null) as ColorKey | null,
        color3: (color3 || null) as ColorKey | null,
        chip_status: chipStatus,
        chip: chipStatus === 'known' ? chip : null,
        gender,
        spayed: fixedToSpayed(fixed),
        lat: location.lat,
        lng: location.lng,
        notes: notes.trim() || null,
        temperament,
        willing_to_foster: willingToFoster,
        willing_to_adopt: willingToAdopt,
        sighted_at: toSightedAt(selectedDate, selectedTime),
        source_locale: i18n.resolvedLanguage || i18n.language || 'en',
      })

      try {
        for (const image of prepared) {
          await uploadSightingImage(created.id, image)
        }
      } catch (photoError) {
        onSubmitted({
          id: created.id,
          animal_id: created.animal_id,
          type: created.type,
          lat: created.lat,
          lng: created.lng,
          sighted_at: created.sighted_at,
          color1: created.color1,
        })
        setSubmitError(
          photoError instanceof Error && photoError.message
            ? `${t('forms.photoUploadFailed')} ${photoError.message}`
            : t('forms.photoUploadFailed'),
        )
        setSubmitting(false)
        return
      }

      onSubmitted({
        id: created.id,
        animal_id: created.animal_id,
        type: created.type,
        lat: created.lat,
        lng: created.lng,
        sighted_at: created.sighted_at,
        color1: created.color1,
      })

      const notifyOutcome = await resolvePostCreateNotifications({
        kind: 'sighting',
        id: created.id,
      })
      setSubmitting(false)
      if (notifyOutcome === 'prompt') {
        setNotifyPrompt({ id: created.id, type: created.type })
      } else {
        finishAfterNotify()
      }
    } catch (caught) {
      setSubmitError(
        caught instanceof Error && caught.message
          ? caught.message
          : t('status.error'),
      )
      setSubmitting(false)
    }
  }

  function chooseCurrentLocation() {
    setLocationPending(true)
    setLocationError(false)
    navigator.geolocation?.getCurrentPosition(
      ({ coords }) => {
        setLocation({ lat: coords.latitude, lng: coords.longitude })
        setLocationMode('current')
        setLocationPending(false)
      },
      () => {
        setLocationError(true)
        setLocationPending(false)
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    )
    if (!navigator.geolocation) {
      setLocationError(true)
      setLocationPending(false)
    }
  }

  if (!open) return null

  if (notifyPrompt) {
    return (
      <NotificationOptInPrompt
        kind="sighting"
        reportId={notifyPrompt.id}
        animalType={notifyPrompt.type}
        onDone={finishAfterNotify}
        onSkip={finishAfterNotify}
      />
    )
  }

  if (showCareInformation) {
    return (
      <div
        className="modal-backdrop"
        role="presentation"
        onClick={finishAfterCare}
      >
        <div
          className="modal modal-wide"
          role="dialog"
          aria-modal="true"
          aria-labelledby="care-info-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id="care-info-title">{t('sighting.careTitle')}</h2>
          <ul>
            <li>{t('sighting.careAdvice.safe')}</li>
            <li>{t('sighting.careAdvice.separate')}</li>
            <li>{t('sighting.careAdvice.vet')}</li>
            <li>{t('sighting.careAdvice.medicine')}</li>
            <li>{t('sighting.careAdvice.owner')}</li>
          </ul>
          <p className="field-help">{t('sighting.careDisclaimer')}</p>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={finishAfterCare}
            >
              {t('panel.closePanel')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal-wide sighting-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-sighting-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="add-sighting-title">{t('forms.addSightingTitle')}</h2>
        <form className="sighting-form" onSubmit={(e) => void handleSubmit(e)}>
          <div className="sighting-form-columns">
            <div className="sighting-form-column">
              <div className="section-heading">
                <span>01</span>
                <h3>{t('forms.animalDetails')}</h3>
              </div>
              <AnimalTypePicker
                idPrefix="sighting"
                value={type}
                onChange={(nextType) => {
                  setType(nextType)
                  setShowTypeError(false)
                }}
                showRequiredError={showTypeError}
              />
              <AnimalReportFields
                idPrefix="sighting"
                color1={color1}
                color2={color2}
                color3={color3}
                gender={gender}
                fixed={fixed}
                chipStatus={chipStatus}
                chip={chip}
                onColor1={setColor1}
                onColor2={setColor2}
                onColor3={setColor3}
                onGender={setGender}
                onFixed={setFixed}
                onChipStatus={(status) => {
                  setChipStatus(status)
                  if (status !== 'known') setChip('')
                }}
                onChip={setChip}
              />
              <div className="field">
                <label htmlFor="sighting-temperament">{t('fields.temperament')}</label>
                <select
                  id="sighting-temperament"
                  value={temperament}
                  onChange={(event) =>
                    setTemperament(event.target.value as Temperament)
                  }
                >
                  {TEMPERAMENTS.map((key) => (
                    <option key={key} value={key}>
                      {t(`temperament.${key}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field notes-field">
                <label htmlFor="sighting-notes">{t('fields.notes')}</label>
                <textarea
                  id="sighting-notes"
                  rows={5}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder={t('forms.sightingNotesPlaceholder')}
                />
              </div>
            </div>

            <div className="sighting-form-column">
              <div className="section-heading">
                <span>02</span>
                <h3>{t('forms.whenAndWhere')}</h3>
              </div>
              <SightingDateField
                dateMode={dateMode}
                selectedDate={selectedDate}
                today={today}
                onSelectToday={() => {
                  setDateMode('today')
                  setSelectedDate(localDateValue())
                }}
                onSelectDate={(value) => {
                  setDateMode('custom')
                  setSelectedDate(value)
                }}
              />
              <SightingTimeField
                timeMode={timeMode}
                selectedTime={selectedTime}
                disabledNow={isDifferentDate}
                onSelectNow={() => {
                  setTimeMode('now')
                  setSelectedTime(localTimeValue())
                }}
                onSelectTime={(value) => {
                  setTimeMode('custom')
                  setSelectedTime(value)
                }}
              />

              <fieldset className="report-section">
                <legend>{t('fields.location')}</legend>
                <div className="choice-pair">
                  <button
                    type="button"
                    className={`choice-button${locationMode === 'current' ? ' selected' : ''}`}
                    onClick={chooseCurrentLocation}
                    disabled={locationPending}
                  >
                    {locationMode === 'current' && <span aria-hidden="true">✓</span>}
                    {locationPending
                      ? t('sighting.findingLocation')
                      : t('sighting.currentLocation')}
                  </button>
                  <button
                    type="button"
                    className={`choice-button${locationMode === 'map' ? ' selected' : ''}`}
                    onClick={onRequestMapPick}
                  >
                    {locationMode === 'map' && <span aria-hidden="true">✓</span>}
                    {locationMode === 'map' && location
                      ? formatCoordinates(location)
                      : t('sighting.pickOnMap')}
                  </button>
                </div>
                {locationError && (
                  <p className="field-error" role="alert">
                    {t('sighting.locationRequired')}
                  </p>
                )}
              </fieldset>

              <PhotoPicker
                idPrefix="sighting"
                images={images}
                onImages={setImages}
                required
                sightingGuidance
                showRequiredError={showPhotoError}
              />
            </div>
          </div>
          <div className="sighting-support-options">
            <fieldset className="report-section">
              <legend>{t('sighting.adoptQuestion')}</legend>
              <div className="choice-pair">
                <button
                  type="button"
                  className={`choice-button${willingToAdopt ? ' selected' : ''}`}
                  onClick={() => setWillingToAdopt(true)}
                >
                  {t('sighting.yes')}
                </button>
                <button
                  type="button"
                  className={`choice-button${!willingToAdopt ? ' selected' : ''}`}
                  onClick={() => setWillingToAdopt(false)}
                >
                  {t('sighting.no')}
                </button>
              </div>
            </fieldset>
            <fieldset className="report-section">
              <legend>{t('sighting.fosterQuestion')}</legend>
              <div className="choice-pair">
                <button
                  type="button"
                  className={`choice-button${willingToFoster ? ' selected' : ''}`}
                  onClick={() => setWillingToFoster(true)}
                >
                  {t('sighting.yes')}
                </button>
                <button
                  type="button"
                  className={`choice-button${!willingToFoster ? ' selected' : ''}`}
                  onClick={() => setWillingToFoster(false)}
                >
                  {t('sighting.no')}
                </button>
              </div>
            </fieldset>
          </div>
          {submitError && (
            <p className="field-error" role="alert">
              {submitError}
            </p>
          )}
          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              {t('actions.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('status.submitting') : t('actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
